import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, TextField, Typography, MenuItem, FormControlLabel, Switch, Alert } from '@mui/material';
import { Save, ArrowBack } from '@mui/icons-material';
import accountingService from '../../services/accounting.service';
import { useLanguage } from '../../i18n/LanguageContext';

const ACCOUNT_TYPES = ['ASSET', 'LIABILITY', 'EQUITY', 'REVENUE', 'EXPENSE'];

const AccountForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const isEdit = Boolean(id);
  const [error, setError] = useState('');
  const [accounts, setAccounts] = useState<any[]>([]);
  const [form, setForm] = useState({
    accountCode: '', accountName: '', accountType: 'ASSET',
    parentAccountId: '' as any, balance: 0, isActive: true, description: ''
  });

  useEffect(() => {
    loadAccounts();
    if (isEdit) loadAccount();
  }, []);

  const loadAccounts = async () => {
    try {
      const res = await accountingService.getAccounts();
      setAccounts(res.data);
    } catch (err) { console.error(err); }
  };

  const loadAccount = async () => {
    try {
      const res = await accountingService.getAccount(Number(id));
      const a = res.data;
      setForm({
        accountCode: a.accountCode, accountName: a.accountName,
        accountType: a.accountType, parentAccountId: a.parentAccountId || '',
        balance: a.balance, isActive: a.isActive, description: a.description || ''
      });
    } catch (err) { console.error(err); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      const data = {
        ...form,
        parentAccountId: form.parentAccountId || null,
      };
      if (isEdit) {
        await accountingService.updateAccount(Number(id), data);
      } else {
        await accountingService.createAccount(data);
      }
      navigate('/accounts');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error saving account');
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">{isEdit ? t('accounts.edit') : t('accounts.new')}</Typography>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/accounts')}>
          Back to List
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField fullWidth required label={t('accounts.account_code')} value={form.accountCode}
          onChange={e => setForm({ ...form, accountCode: e.target.value })} />
        <TextField fullWidth required label={t('accounts.account_name')} value={form.accountName}
          onChange={e => setForm({ ...form, accountName: e.target.value })} />
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField fullWidth select required label={t('accounts.account_type')} value={form.accountType}
          onChange={e => setForm({ ...form, accountType: e.target.value })}>
          {ACCOUNT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>
        <TextField fullWidth select label="Parent Account" value={form.parentAccountId}
          onChange={e => setForm({ ...form, parentAccountId: e.target.value })}>
          <MenuItem value="">None</MenuItem>
          {accounts.filter(a => !id || a.id !== Number(id)).map((a: any) => (
            <MenuItem key={a.id} value={a.id}>{a.accountCode} - {a.accountName}</MenuItem>
          ))}
        </TextField>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField fullWidth type="number" label="Balance" value={form.balance}
          onChange={e => setForm({ ...form, balance: Number(e.target.value) })}
          inputProps={{ step: '0.01' }} />
        <TextField fullWidth label={t('common.description')} value={form.description}
          onChange={e => setForm({ ...form, description: e.target.value })} />
      </Box>

      <FormControlLabel control={
        <Switch checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} />
      } label="Active" />

      <Box sx={{ mt: 3 }}>
        <Button type="submit" variant="contained" startIcon={<Save />} size="large">
          {isEdit ? 'Update Account' : 'Create Account'}
        </Button>
      </Box>
    </Box>
  );
};

export default AccountForm;
